<!-- src/components/ImageUploader.vue -->
<template>
  <div class="w-full flex flex-col space-y-6 text-left">
    
    <!-- OPTIONAL LABEL -->
    <label v-if="label" class="block text-[10px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-1">
      {{ label }}
    </label>
    
    <!-- COMMAND CENTRE DRAG & DROP ZONE -->
    <div 
      class="relative w-full flex flex-col items-center justify-center p-8 md:p-12 border-2 border-dashed rounded-[32px] transition-all duration-300 overflow-hidden group"
      :class="[
        isDragging 
          ? 'border-[#20FCF1] bg-[#0A1630] scale-[1.02] shadow-2xl shadow-[#20FCF1]/20' 
          : 'border-[#0A1630]/20 bg-[#0A1630] hover:border-[#20FCF1]/50'
      ]"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <!-- Background Ambient Icon -->
      <div class="absolute top-0 right-0 p-8 opacity-5 pointer-events-none transition-opacity group-hover:opacity-10">
        <svg class="h-32 w-32 text-[#20FCF1]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
          <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
        </svg>
      </div>

      <!-- Foreground UI -->
      <div class="space-y-6 text-center relative z-10 flex flex-col items-center">
        <div class="bg-[#20FCF1]/10 p-4 rounded-2xl">
          <svg 
            class="h-10 w-10 transition-colors duration-300" 
            :class="isDragging ? 'text-[#20FCF1]' : 'text-[#20FCF1]/70'"
            stroke="currentColor" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
        </div>

        <div class="flex flex-col items-center gap-4">
          <label :for="id" class="cursor-pointer px-8 py-4 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
            <span>Initialise Upload</span>
            <input :id="id" :name="id" type="file" class="sr-only" multiple @change="handleFileChange" accept="image/png, image/jpeg, image/gif">
          </label>
          <p class="text-[10px] font-black uppercase tracking-widest text-slate-400">Or Drag & Drop Assets Here</p>
        </div>
        
        <p class="text-[9px] font-black text-[#20FCF1] uppercase tracking-[0.2em] px-4 py-1.5 bg-[#20FCF1]/10 rounded-lg shadow-inner">
          PNG, JPG, GIF • MAX {{ maxSize }}MB
        </p>
      </div>
    </div>

    <!-- ERROR PROTOCOL ALERT -->
    <div v-if="errorMessage" class="w-full p-5 bg-rose-50 border-l-4 border-rose-500 rounded-2xl flex items-center gap-4 animate-fadeIn shadow-sm">
      <svg class="h-6 w-6 text-rose-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
      </svg>
      <p class="text-[10px] font-black uppercase tracking-widest text-rose-600 leading-relaxed">{{ errorMessage }}</p>
    </div>

    <!-- SYNCHRONISED ASSETS REGISTRY -->
    <div v-if="selectedFiles.length > 0" class="w-full space-y-4 animate-fadeIn">
       <div class="flex items-center gap-3 mb-2 px-2">
         <span class="bg-[#0A1630] text-[#20FCF1] rounded-lg px-3 py-1 text-[10px] font-black shadow-sm">
           {{ selectedFiles.length }}
         </span>
         <h3 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.2em]">Synchronised Assets</h3>
       </div>
       
       <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
         <div v-for="(file, index) in selectedFiles" :key="index" class="flex items-center justify-between p-4 bg-white rounded-[24px] border border-slate-200 shadow-sm hover:shadow-md hover:border-[#20FCF1] transition-all group">
          <div class="flex items-center space-x-4 overflow-hidden">
              <div class="relative w-14 h-14 shrink-0 rounded-xl overflow-hidden border border-slate-100 shadow-inner bg-slate-50">
                <img :src="file.preview" class="w-full h-full object-cover" alt="Asset Preview">
              </div>
              <div class="flex flex-col min-w-0 pr-4">
                <span class="text-xs font-black text-[#0A1630] truncate leading-tight mb-1">{{ file.file.name }}</span>
                <span class="text-[9px] text-[#20FCF1] uppercase font-black tracking-widest">{{ (file.file.size / 1024 / 1024).toFixed(2) }} MB</span>
              </div>
          </div>
          <button @click="removeFile(index)" type="button" class="p-3 rounded-xl text-slate-300 hover:text-rose-500 hover:bg-rose-50 shrink-0 transition-all active:scale-95" title="Remove Asset">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
              </svg>
          </button>
         </div>
       </div>
    </div>

  </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
  id: {
    type: String,
    default: 'file-upload'
  },
  label: {
    type: String,
    default: ''
  },
  maxSize: { 
    type: Number,
    default: 10 // 🛡️ GLOBAL STANDARD UPGRADED TO 10MB
  }
});

const emit = defineEmits(['update:files']);

const selectedFiles = ref([]);
const errorMessage = ref('');
const isDragging = ref(false);

/**
 * Handle Drag events
 */
const onDragOver = () => {
  isDragging.value = true;
};

const onDragLeave = () => {
  isDragging.value = false;
};

const onDrop = (event) => {
  isDragging.value = false;
  const files = event.dataTransfer.files;
  if (files) {
    processFiles(files);
  }
};

/**
 * Handle manual selection
 */
const handleFileChange = (event) => {
  const files = event.target.files;
  if (files) {
    processFiles(files);
  }
  // Reset input to allow re-selection of the same file if necessary
  event.target.value = '';
};

/**
 * Common processing logic
 */
const processFiles = (fileList) => {
  errorMessage.value = '';
  
  // Convert FileList to array
  const filesArray = Array.from(fileList);

  for (const file of filesArray) {
    // 1. Validation: Size (Converted to Bytes)
    if (file.size > props.maxSize * 1024 * 1024) {
      errorMessage.value = `Asset "${file.name}" exceeds the authorised ${props.maxSize}MB limit.`;
      continue;
    }
    
    // 2. Validation: Type
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
        errorMessage.value = `Asset "${file.name}" is not a supported image format.`;
        continue;
    }

    // 3. Create Preview
    const reader = new FileReader();
    reader.onload = (e) => {
      selectedFiles.value.push({ 
        file: file, 
        preview: e.target.result 
      });
      emitUpdate();
    };
    reader.readAsDataURL(file);
  }
};

const removeFile = (index) => {
  selectedFiles.value.splice(index, 1);
  emitUpdate();
};

const emitUpdate = () => {
  emit('update:files', selectedFiles.value.map(f => f.file));
};
</script>

<style scoped>
.animate-fadeIn { 
  animation: fadeIn 0.4s ease-out forwards; 
}
@keyframes fadeIn { 
  from { opacity: 0; transform: translateY(10px); } 
  to { opacity: 1; transform: translateY(0); } 
}
</style>